import { Component, OnInit, Input } from '@angular/core';
import { IObservationDefinition, ICodeableConcept, IAuditEvent, IPatient, ICarePlan } from '@ahryman40k/ts-fhir-types/lib/R4';
import { ModalController, AlertController } from '@ionic/angular';
import { FormGroup, FormArray, FormControl } from '@angular/forms';
import { AbstractAddComponent } from '../../../../../shared/abstracts/add/abstract-add/abstract-add.component';
import { TranslatePipe } from '../../../../../shared/translate/pipes/translate.pipe';
import { dataToAtomicAuditEventDetail, dataToAggregatedAuditEventDetail } from '../../../../functions/observation-definition.function';
import { Category } from '../../../../models/category.model';

@Component({
  selector: 'inc-observation-definition-add-atomic',
  templateUrl: './observation-definition-add-atomic.component.html',
  styleUrls: ['./observation-definition-add-atomic.component.scss'],
})
export class ObservationDefinitionAddAtomicComponent extends AbstractAddComponent<{ data: IObservationDefinition, audit: IAuditEvent }[]>
  implements OnInit {
  @Input() isModal: boolean;
  @Input() patient: IPatient;
  @Input() user: any;
  @Input() careplan: ICarePlan;
  @Input() category: Category;

  careplanCode: ICodeableConcept;
  parameterCodes: ICodeableConcept[] = [];
  units: string[] = [];
  noMax: boolean[] = [];
  percent: boolean[] = [];
  lowerLimit: number[] = [];
  upperLimit: number[] = [];
  allSelect: boolean;

  index = 0;

  constructor(private modalController: ModalController, private alertController: AlertController, private translatePipe: TranslatePipe) {
    super();
  }

  ngOnInit() {
    this.setCategoryValues();
    this.addAllSelect();
    this.setFormValue();
  }

  setCategoryValues() {
    if (this.category) {
      this.careplanCode = this.category.carePlan.value;
      this.allSelect = this.category.carePlan.allSelect ? this.category.carePlan.allSelect.atomic : false;

      Object.values(this.category.parameters).map(value => {
        this.parameterCodes.push(value.value);
        this.units.push(value.unit);
        this.noMax.push(value.noMax);
        this.percent.push(value.percent);
        this.lowerLimit.push(value.lowerLimit);
        this.upperLimit.push(value.upperLimit);
      });
    }
  }

  addAllSelect() {
    if (this.allSelect && !this.inData) {
      this.parameterCodes.unshift({ text: 'ALL' });
    }
  }

  setFormValue() {
    if (this.inData && this.inData[0] && this.inData[0].data) {
      this.form.patchValue(this.inData[0].data);
      if (this.parameterCodes) {
        this.form.patchValue({
          qualifiedInterval: [{
            context: this.parameterCodes.filter(value => value.text === this.inData[0].data.qualifiedInterval[0].context.text)[0]
          }]
        });
        this.index = this.parameterCodes.indexOf(this.form.value.qualifiedInterval[0].context);
      }
    } else if (this.parameterCodes) {
      this.form.patchValue({
        code: this.careplanCode,
        qualifiedInterval: [{
          context: this.parameterCodes[0],
          range: {
            low: {
              unit: this.units[0]
            },
            high: {
              unit: this.units[0]
            }
          }
        }]
      });
    }
  }

  resetForm() {
    return new FormGroup({
      id: new FormControl(),
      code: new FormControl(),
      method: new FormGroup({
        text: new FormControl('atomic')
      }),
      qualifiedInterval: new FormArray([
        new FormGroup({
          context: new FormControl(),
          range: new FormGroup({
            low: new FormGroup({
              value: new FormControl(''),
              unit: new FormControl('')
            }),
            high: new FormGroup({
              value: new FormControl(''),
              unit: new FormControl('')
            })
          })
        })
      ]),
      meta: new FormGroup({
        lastUpdated: new FormControl(new Date().toISOString())
      }),
      text: new FormGroup({
        div: new FormControl('')
      }),
      resourceType: new FormControl('ObservationDefinition')
    });
  }

  close(data?: { data: IObservationDefinition, audit: IAuditEvent }[]) {
    if (data) {
      Object.values(data).map(value => {
        if (!value.data.qualifiedInterval[0].range.high.value) {
          delete value.data.qualifiedInterval[0].range.high;
        }
      });
    }
    if (this.isModal) {
      data ? this.modalController.dismiss(data) : this.modalController.dismiss();
    } else {
      this.save(data);
    }
  }

  add(data: IObservationDefinition) {
    const allData: { data: IObservationDefinition, audit: IAuditEvent }[] = [];

    if (this.allSelect && this.index === 0 && !this.inData) {
      for (let i = 1; i < this.parameterCodes.length; i++) {
        const dataClone: IObservationDefinition = JSON.parse(JSON.stringify(data));
        dataClone.code = this.careplanCode;
        dataClone.qualifiedInterval[0].context = this.parameterCodes[i];
        dataClone.meta.lastUpdated = new Date().toISOString();
        allData.push({ data: dataClone, audit: this.createAuditEvent(dataClone) });
      }
    } else {
      allData.push({ data, audit: this.createAuditEvent(data) });
    }
    this.close(allData);
  }

  selectChange(event: { detail: { value: ICodeableConcept } }) {
    this.index = this.parameterCodes.indexOf(event.detail.value);

    this.form.patchValue({
      code: this.careplanCode,
      qualifiedInterval: [{
        context: this.parameterCodes[this.index],
        range: {
          low: {
            unit: this.units[this.index]
          },
          high: {
            unit: this.units[this.index]
          }
        }
      }]
    });
  }

  checkPercentOrLimit(control: FormControl) {
    if (this.percent[this.index] && control.value > 100 ||
      control.value < this.lowerLimit[this.index] || control.value > this.upperLimit[this.index]) {
      control.setErrors({ error: 'value' });
    }
  }

  async openAlert(data?: IObservationDefinition) {
    const alert = await this.alertController.create({
      header: this.translatePipe.transform('EDIT_ALERT'),
      message: this.translatePipe.transform('THERAPY_CHANGE_Q'),
      buttons: [{
        text: this.translatePipe.transform('CANCEL_L'),
        role: 'cancel'
      }, {
        text: this.translatePipe.transform('YES'),
        handler: () => { this.add(data); }
      }]
    });
    await alert.present();
  }

  createAuditEvent(data: IObservationDefinition): IAuditEvent {
    const newAudit: IAuditEvent = {
      id: '',
      agent: [{
        name: this.patient.name[0].text,
        requestor: false,
        who: {
          reference: this.patient.id
        }
      }],
      entity: [{
        description: data.text ? data.text.div : '',
        detail: dataToAtomicAuditEventDetail(this.inData && this.inData[0] && this.inData[0].data ? this.inData[0].data : null, data),
        what: {
          reference: this.careplan.id
        }
      }],
      recorded: new Date().toISOString(),
      source: {
        observer: {
          display: this.user.displayName,
          id: this.user.uid
        }
      },
      type: {
        display: 'atomic'
      },
      resourceType: 'AuditEvent'
    };

    return newAudit;
  }
}
